import React, { FC, useRef } from 'react';
import { IoArrowBack, IoArrowForward } from 'react-icons/io5';

import { Carousel, CarouselProps } from '@mantine/carousel';
import { Flex, Group } from '@mantine/core';
import Autoplay, { AutoplayType } from 'embla-carousel-autoplay';

import { useResponsiveNbCols } from 'components/section/useResponsiveNbCols';
import { Breakpoint } from 'hooks/use-breakpoint';

import cx from './CarouselSection.module.scss';

export type CarouselSectionProps = {
    children: React.ReactNode;
    nbCols?: number;
    cols?: Record<Breakpoint, number>;
    loop?: boolean;
    autoplay?: boolean;
    autoplayDelay?: number;
    isStreaming?: boolean;
    withIndicators?: boolean;
    slideGap?: string | number;
    align?: 'start' | 'center' | 'end';
} & Omit<CarouselProps, 'title'>;

const CarouselSection: FC<CarouselSectionProps> = ({
    children,
    nbCols = 4,
    cols,
    autoplay,
    autoplayDelay = 5000,
    isStreaming,
    withIndicators = false,
    slideGap = 'md',
    align = 'start',
    ...props
}) => {
    const autoplayRef = useRef<AutoplayType>(Autoplay({ delay: autoplayDelay, stopOnMouseEnter: true }));

    const responsiveNbCols = useResponsiveNbCols(nbCols, cols);

    if (!responsiveNbCols) return null;

    // At one column, half of the next slide shows so it is clear the row scrolls sideways
    const isPeeking = responsiveNbCols === 1;
    const slidesShown = isPeeking ? 1.5 : responsiveNbCols;

    // toArray drops empty children
    const slides = React.Children.toArray(children);

    if (slides.length <= responsiveNbCols) {
        return (
            <Group gap={slideGap} w="100%" align="stretch" justify={align}>
                {slides.map((child, index) => (
                    <Flex
                        key={index}
                        data-carousel-section-item
                        justify="center"
                        w={`calc(100% / ${responsiveNbCols} - var(--mantine-spacing-${slideGap}) * (${responsiveNbCols} - 1) / ${responsiveNbCols})`}
                    >
                        {child}
                    </Flex>
                ))}
            </Group>
        );
    }

    return (
        <Carousel
            classNames={cx}
            emblaOptions={{
                align,
                containScroll: 'trimSnaps',
                slidesToScroll: responsiveNbCols,
            }}
            slideGap={slideGap}
            slideSize={`${100 / slidesShown}%`}
            previousControlIcon={<IoArrowBack />}
            nextControlIcon={<IoArrowForward />}
            plugins={autoplay ? [autoplayRef.current as any] : []}
            withIndicators={withIndicators}
            withControls={!isPeeking}
            data-peeking={isPeeking || undefined}
            key={responsiveNbCols}
            {...props}
        >
            {slides.map((child, index) => (
                <Carousel.Slide key={index}>{child}</Carousel.Slide>
            ))}
        </Carousel>
    );
};

export { CarouselSection };
